import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { getCustomersByUser, updateCustomer, getTelzioMe, getPitStop, getMySprint } from '../actions/api';
import { refreshSession } from '../actions/auth';
import { history } from '../routers/AppRouter';
import { STAGES, REFERRAL_POINTS, MAX_REFERRALS, tally } from '../scoring';
import { TEAM_LIVERY } from '../theme';

export const ReferralStepper = ({ value, onChange }) => (
  <div className="stepper" role="group" aria-label="Referrals">
    <button type="button" onClick={() => onChange(value - 1)} disabled={value <= 0} aria-label="Remove a referral">−</button>
    <span className="value" aria-live="polite"><b>{value}</b> {value === 1 ? 'referral' : 'referrals'}</span>
    <button type="button" onClick={() => onChange(value + 1)} disabled={value >= MAX_REFERRALS} aria-label="Add a referral">+</button>
  </div>
);

export const StageToggles = ({ customer, onToggle }) => (
  <div className="stage-toggles">
    {STAGES.map((s) => (
      <button key={s.key} type="button" className={`stage-toggle ${s.key}`}
        aria-pressed={!!customer[s.key]} onClick={() => onToggle(s.key)}>
        {s.short}
      </button>
    ))}
  </div>
);

const customerPoints = (c) => tally([c]).points;

const PIT_STOP_CHECK_MS = 5 * 60 * 1000;
const SPRINT_CHECK_MS = 20000;

const formatClock = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

// Purple banner while a sprint runs: what it is, how it ends, where your team stands.
const SprintBanner = ({ data, now }) => {
  const { sprint, team } = data;
  const goal = [
    sprint.targetPoints && `first team to ${sprint.targetPoints} points wins`,
    sprint.endsAt && (sprint.targetPoints ? 'or the leader when time runs out' : 'the leader when time runs out wins'),
  ].filter(Boolean).join(', ');
  return (
    <div className="sprint-banner" role="status">
      <span className="sprint-flag" aria-hidden="true" />
      <div className="sprint-banner-text">
        <h2>Sprint Race is on</h2>
        <p>Only points from now count: {goal}.</p>
      </div>
      {team && (
        <div className="sprint-banner-stat">
          <span className="n">P{team.rank}</span>
          <span className="l">{TEAM_LIVERY[team.color].label} team, {team.points} pts</span>
        </div>
      )}
      {sprint.endsAt && (
        <div className="sprint-banner-stat">
          <span className="n">{formatClock(new Date(sprint.endsAt) - now)}</span>
          <span className="l">left</span>
        </div>
      )}
    </div>
  );
};

const SPRINT_COLUMNS = [
  ...STAGES.map((s) => ({ key: s.key, label: s.label })),
  { key: 'referrals', label: 'Referrals' },
  { key: 'points', label: 'Points' },
];

// Your sprint numbers next to your whole-bootcamp numbers.
const SprintTable = ({ sprintName, mine, totals }) => (
  <div className="panel sprint-table">
    <div className="table-scroll">
      <table className="stat-table">
        <thead>
          <tr>
            <th>You</th>
            {SPRINT_COLUMNS.map((c) => <th key={c.key}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr className="sprint-row">
            <td>{sprintName}</td>
            {SPRINT_COLUMNS.map((c) => <td key={c.key} className={c.key === 'points' ? 'points' : ''}>{mine[c.key]}</td>)}
          </tr>
          <tr>
            <td>Whole bootcamp</td>
            {SPRINT_COLUMNS.map((c) => <td key={c.key} className={c.key === 'points' ? 'points' : ''}>{totals[c.key]}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);
const PIT_STOP_DISMISSED_KEY = 'tnp.pitStopDismissed';

// Shown when the race is on and the rep hasn't logged anything for 45 minutes.
const PitStop = ({ pitStop, onDismiss }) => (
  <div className="pit-stop" role="status">
    <div>
      <h2>Pit stop</h2>
      <p>Nothing logged in {pitStop.idleMinutes} minutes. {pitStop.suggestions.length ? 'These customers are closest to points:' : 'Time to get back on the phone.'}</p>
      {pitStop.suggestions.length > 0 && (
        <ul>
          {pitStop.suggestions.map((c) => <li key={c.id}><b>{c.name}</b>: {c.next}</li>)}
        </ul>
      )}
    </div>
    <Button color="secondary" onClick={onDismiss}>Got it</Button>
  </div>
);

// Calls from Telzio plus a shortcut into the rep's Telzio account.
const PhonePanel = ({ telzio }) => {
  if (!telzio) return null;
  const counts = telzio.calls;
  return (
    <div className="panel phone-panel">
      <div className="phone-stats">
        {counts ? (
          <>
            <div><span className="n">{counts.today}</span><span className="l">Calls today</span></div>
            <div><span className="n">{counts.bootcamp}</span><span className="l">Calls this bootcamp</span></div>
          </>
        ) : (
          <p className="phone-note">
            {telzio.tracking
              ? 'Your call count shows here once your Telzio account is linked. Ask your trainer to link it.'
              : 'Open your Telzio phone to start calling.'}
          </p>
        )}
      </div>
      <div className="phone-action">
        <Button variant="contained" color="secondary" href={telzio.loginUrl} target="_blank" rel="noopener">
          Open Telzio
        </Button>
        {telzio.username && <span className="phone-user">Telzio user: {telzio.username}</span>}
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [telzio, setTelzio] = useState(null);
  const [pitStop, setPitStop] = useState(null);
  const [sprint, setSprint] = useState(null);
  const [now, setNow] = useState(Date.now());
  const sprintTimer = useRef(null);

  const refreshSprint = () => getMySprint().then(setSprint).catch(() => {});

  // Sprint theme reaches the top bar and page background too.
  useEffect(() => {
    document.body.classList.toggle('sprint-mode', !!sprint);
    return () => document.body.classList.remove('sprint-mode');
  }, [sprint]);
  const pending = useRef({});
  const [onTeam, setOnTeam] = useState(!!Cookies.get('team'));

  useEffect(() => {
    getCustomersByUser().then(setCustomers).catch(() => setCustomers([]));
    refreshSession().then(({ team }) => setOnTeam(!!team)).catch(() => {});
    getTelzioMe().then(setTelzio).catch(() => {});
    // A dismissed pit stop stays dismissed until the rep logs something and goes quiet again.
    const checkPitStop = () => getPitStop().then((p) => {
      let dismissed = null;
      try { dismissed = window.sessionStorage.getItem(PIT_STOP_DISMISSED_KEY); } catch (e) { /* optional */ }
      setPitStop(p && p.lastActivityAt !== dismissed ? p : null);
    }).catch(() => {});
    checkPitStop();
    const pitTimer = setInterval(checkPitStop, PIT_STOP_CHECK_MS);
    refreshSprint();
    const sprintPoll = setInterval(refreshSprint, SPRINT_CHECK_MS);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(pitTimer);
      clearInterval(sprintPoll);
      clearInterval(tick);
      clearTimeout(sprintTimer.current);
    };
  }, []);

  // Optimistic update; referral clicks are debounced so rapid taps send one request.
  const save = (customer, changes, delay = 0) => {
    const next = { ...customer, ...changes };
    setCustomers((list) => list.map((c) => (c.id === next.id ? next : c)));
    clearTimeout(pending.current[next.id]);
    pending.current[next.id] = setTimeout(() => {
      updateCustomer(next.id, next)
        .then(() => {
          // Sprint numbers come from the server's log; refresh shortly after edits settle.
          clearTimeout(sprintTimer.current);
          sprintTimer.current = setTimeout(refreshSprint, 600);
        })
        .catch(() => setSaveError('Couldn’t save that change. Check your connection and try again.'));
    }, delay);
  };

  const totals = useMemo(() => tally(customers || []), [customers]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (customers || []).filter((c) => !q || c.name.toLowerCase().includes(q));
  }, [customers, search]);

  const openRename = (c) => {
    setRenaming(c);
    setNewName(c.name);
  };
  const submitRename = () => {
    save(renaming, { name: newName.trim() });
    setRenaming(null);
  };

  return (
    <div className={`page${sprint ? ' sprint-mode' : ''}`}>
      <div className="page-head">
        <div>
          <h1>My customers</h1>
          <p>Tap a stage as each customer moves through it. Changes save on their own.</p>
        </div>
        <Button variant="contained" size="large" onClick={() => history.push('/addcustomer')} disabled={!onTeam}>
          Add customer
        </Button>
      </div>

      {!onTeam && (
        <div className="notice" role="status">
          You’re not on a team yet, so you can’t add customers. Your trainer will assign you one.
        </div>
      )}

      {pitStop && (
        <PitStop pitStop={pitStop} onDismiss={() => {
          try { window.sessionStorage.setItem(PIT_STOP_DISMISSED_KEY, pitStop.lastActivityAt); } catch (e) { /* optional */ }
          setPitStop(null);
        }} />
      )}

      {sprint && <SprintBanner data={sprint} now={now} />}
      {sprint && <SprintTable sprintName="This Sprint Race" mine={sprint.mine} totals={totals} />}

      <PhonePanel telzio={telzio} />

      <div className="panel funnel" aria-label="Your totals">
        {STAGES.map((s) => (
          <div key={s.key} className="funnel-step">
            <span className="n">{totals[s.key]}</span>
            <span className="l">{s.label}</span>
          </div>
        ))}
        <div className="funnel-points">
          <span className="n">{totals.points}</span>
          <span className="l">Your points</span>
        </div>
      </div>

      <div className="panel">
        {customers === null ? (
          <div className="loading-line">Loading your customers…</div>
        ) : customers.length === 0 ? (
          <div className="empty-state">
            <h2>No customers yet</h2>
            <p>Add each customer you reach out to, then mark their progress here as it happens.</p>
            {onTeam && <Button variant="contained" onClick={() => history.push('/addcustomer')}>Add your first customer</Button>}
          </div>
        ) : (
          <>
            <div className="customer-tools">
              <TextField size="small" placeholder="Search customers" value={search}
                onChange={(e) => setSearch(e.target.value)} inputProps={{ 'aria-label': 'Search customers' }} />
              <span className="count">{visible.length} of {customers.length}</span>
            </div>
            {visible.map((c) => (
              <div className="customer-row" key={c.id}>
                <div>
                  <button type="button" className="customer-name" onClick={() => openRename(c)}
                    title="Rename customer">{c.name || 'Unnamed customer'}</button>
                  <span className="customer-points">{customerPoints(c)} pts</span>
                </div>
                <StageToggles customer={c} onToggle={(key) => save(c, { [key]: !c[key] })} />
                <ReferralStepper value={c.referrals || 0}
                  onChange={(v) => save(c, { referrals: Math.max(0, Math.min(MAX_REFERRALS, v)) }, 400)} />
              </div>
            ))}
            {visible.length === 0 && <div className="empty-state"><p>No customers match “{search}”.</p></div>}
          </>
        )}
      </div>

      <p style={{ color: '#5B6670', fontSize: '0.9rem', marginTop: 16 }}>
        Points: quality conversation 1, appointment set 2, shown 3, sold 5, each referral {REFERRAL_POINTS}.
      </p>

      <Dialog open={!!renaming} onClose={() => setRenaming(null)} fullWidth maxWidth="xs">
        <form onSubmit={(e) => { e.preventDefault(); submitRename(); }}>
          <DialogTitle>Rename customer</DialogTitle>
          <DialogContent>
            <TextField autoFocus label="Customer name" value={newName} onChange={(e) => setNewName(e.target.value)}
              sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenaming(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!newName.trim()}>Save name</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={!!saveError} autoHideDuration={6000} onClose={() => setSaveError('')} message={saveError} />
    </div>
  );
};

export default DashboardPage;
